import React, { useEffect, useRef, useState } from "react";
import api from "../api/client";
import { Upload, Trash2, Calendar, HardHat, Loader2, Image as ImageIcon } from "lucide-react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChantierGallery({ immeubleId }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [datePhoto, setDatePhoto] = useState(new Date().toISOString().slice(0, 10));
  const [caption, setCaption] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/media", { params: { owner_type: "immeuble", owner_id: immeubleId } });
      setItems((data || []).filter((m) => m.kind === "chantier").sort((a, b) => (b.date_photo || "").localeCompare(a.date_photo || "")));
    } catch { /* */ }
  };

  useEffect(() => { if (immeubleId) load(); }, [immeubleId]);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 8 * 1024 * 1024) { toast.error(`${file.name} > 8 Mo, ignoré`); continue; }
        const fd = new FormData();
        fd.append("owner_type", "immeuble");
        fd.append("owner_id", immeubleId);
        fd.append("kind", "chantier");
        fd.append("caption", caption);
        fd.append("date_photo", datePhoto);
        fd.append("file", file);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo(s) chantier ajoutée(s)`);
      setCaption("");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cette photo de chantier ?")) return;
    try {
      await api.delete(`/media/${id}`);
      await load();
      toast.success("Photo supprimée");
    } catch { toast.error("Erreur"); }
  };

  // Regrouper par date
  const groups = items.reduce((acc, m) => {
    const k = m.date_photo || "Sans date";
    (acc[k] = acc[k] || []).push(m);
    return acc;
  }, {});

  return (
    <div className="bg-white border border-[#E2E5F1] rounded-xl overflow-hidden" data-testid="chantier-gallery">
      <div className="px-6 py-4 border-b border-[#E2E5F1] flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#FFF4E5] text-[#D48A37] flex items-center justify-center">
            <HardHat className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-display text-lg font-bold text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>État d'avancement du chantier</h3>
            <p className="text-xs text-[#4B5775]">Photos visibles par les clients dans leur espace personnel.</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold">Total photos</div>
          <div className="font-display text-xl font-bold text-[#0F1A3D]">{items.length}</div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Form upload */}
        <div className="bg-[#F8FAFC] border border-[#E2E5F1] rounded-lg p-4 grid grid-cols-1 md:grid-cols-[200px_1fr_auto] gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1">Date de la photo *</Label>
            <Input type="date" value={datePhoto} onChange={(e) => setDatePhoto(e.target.value)} data-testid="chantier-date" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider text-[#4B5775] font-bold block mb-1">Légende (optionnel)</Label>
            <Input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder='ex: "Pose des fondations - bloc A"' data-testid="chantier-caption" />
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="h-10 px-4 rounded-md bg-[#D48A37] hover:bg-[#B97527] text-white text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="chantier-upload-btn"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Ajouter
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
          />
        </div>

        {/* Groupes par date */}
        {Object.keys(groups).length === 0 ? (
          <div className="text-center py-12 text-sm text-[#9CA3B5]">
            <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
            Aucune photo de chantier pour le moment.
          </div>
        ) : (
          Object.entries(groups).map(([date, photos]) => (
            <div key={date} data-testid={`chantier-group-${date}`}>
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-[#E91E63]" />
                <div className="font-display text-base font-bold text-[#0F1A3D]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {date === "Sans date" ? date : new Date(date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                </div>
                <div className="text-xs text-[#9CA3B5]">— {photos.length} photo{photos.length > 1 ? "s" : ""}</div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {photos.map((m) => (
                  <div key={m.id} className="relative group rounded-lg overflow-hidden border border-[#E2E5F1]">
                    <img
                      src={`${BACKEND_URL}/api/media/${m.id}/file`}
                      alt={m.caption || "Chantier"}
                      className="w-full h-32 object-cover"
                    />
                    {m.caption && (
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-xs p-2">
                        {m.caption}
                      </div>
                    )}
                    <button
                      onClick={() => remove(m.id)}
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-[#C83B3B] hover:text-white text-[#0F1A3D] flex items-center justify-center opacity-0 group-hover:opacity-100 transition"
                      aria-label="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
